import { createSlice } from "@reduxjs/toolkit";
import {
  getAllCartsThunk,
  getCartByIdThunk,
  getUserCartThunk,
  clearUserCartThunk,
  getPendingExchangeItemsThunk,
  getExchangeItemDetailsThunk,
  verifyExchangeItemThunk,
  rejectExchangeItemThunk,
  bulkVerifyExchangeItemsThunk,
  getCartAnalyticsThunk,
  getAbandonedCartsThunk,
  markAbandonedCartsThunk,
  clearAdminCartError,
  resetSelectedCart,
  setCartFilter,
  resetExchangeItemDetails,
} from "../thunks/adminCartThunks";

const initialState = {
  // Cart data
  carts: [],
  selectedCart: null,
  userCart: null,
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
  },

  // Exchange items
  pendingExchangeItems: [],
  exchangeItemDetails: null,
  exchangePagination: {
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
  },

  // Analytics
  cartAnalytics: null,
  abandonedCarts: [],
  abandonedPagination: {
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
  },

  // Filters
  filters: {
    status: "ACTIVE",
    search: "",
    hasExchangeItems: null,
    minValue: null,
    maxValue: null,
    sortBy: "lastActivity",
    sortOrder: "desc",
  },

  exchangeFilters: {
    itemType: "",
    sortBy: "addedAt",
    sortOrder: "desc",
  },

  // Loading states
  loading: false,
  loadingList: false,
  loadingExchange: false,
  loadingAnalytics: false,

  // Error handling
  error: null,
  successMessage: null,

  // Constants
  cartStatuses: [
    { value: "ACTIVE", label: "Active" },
    { value: "ABANDONED", label: "Abandoned" },
    { value: "CONVERTED", label: "Converted" },
  ],

  exchangeStatuses: [
    { value: "PENDING_VERIFICATION", label: "Pending Verification" },
    { value: "VERIFIED", label: "Verified" },
    { value: "REJECTED", label: "Rejected" },
  ],

  itemTypes: [
    { value: "GOLD", label: "Gold" },
    { value: "SILVER", label: "Silver" },
    { value: "PLATINUM", label: "Platinum" },
    { value: "DIAMOND", label: "Diamond" },
    { value: "JEWELRY", label: "Jewelry" },
  ],

  conditions: [
    { value: "EXCELLENT", label: "Excellent" },
    { value: "GOOD", label: "Good" },
    { value: "FAIR", label: "Fair" },
    { value: "POOR", label: "Poor" },
  ],
};

const adminCartSlice = createSlice({
  name: "adminCart",
  initialState,
  reducers: {
    setCartFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearCartFilters: (state) => {
      state.filters = initialState.filters;
    },
    setExchangeFilters: (state, action) => {
      state.exchangeFilters = { ...state.exchangeFilters, ...action.payload };
    },
    clearExchangeFilters: (state) => {
      state.exchangeFilters = initialState.exchangeFilters;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Clear error
      .addCase(clearAdminCartError, (state) => {
        state.error = null;
      })
      // Reset selected cart
      .addCase(resetSelectedCart, (state) => {
        state.selectedCart = null;
      })
      // Set filter
      .addCase(setCartFilter, (state, action) => {
        state.filters = { ...state.filters, ...action.payload };
      })
      // Reset exchange item details
      .addCase(resetExchangeItemDetails, (state) => {
        state.exchangeItemDetails = null;
      })

      // Get All Carts
      .addCase(getAllCartsThunk.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(getAllCartsThunk.fulfilled, (state, action) => {
        state.loadingList = false;
        state.carts = action.payload.data || [];
        state.pagination = action.payload.pagination || initialState.pagination;
      })
      .addCase(getAllCartsThunk.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      })

      // Get Cart By ID
      .addCase(getCartByIdThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCartByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedCart = action.payload.data;
      })
      .addCase(getCartByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get User Cart
      .addCase(getUserCartThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserCartThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.userCart = action.payload.data;
      })
      .addCase(getUserCartThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Clear User Cart
      .addCase(clearUserCartThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(clearUserCartThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage =
          action.payload.message || "Cart cleared successfully";
        // Remove cart from list
        state.carts = state.carts.filter(
          (cart) => cart._id !== action.meta.arg.cartId
        );
        if (state.selectedCart?._id === action.meta.arg.cartId) {
          state.selectedCart = null;
        }
      })
      .addCase(clearUserCartThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Pending Exchange Items
      .addCase(getPendingExchangeItemsThunk.pending, (state) => {
        state.loadingExchange = true;
        state.error = null;
      })
      .addCase(getPendingExchangeItemsThunk.fulfilled, (state, action) => {
        state.loadingExchange = false;
        state.pendingExchangeItems = action.payload.data || [];
        state.exchangePagination =
          action.payload.pagination || initialState.exchangePagination;
      })
      .addCase(getPendingExchangeItemsThunk.rejected, (state, action) => {
        state.loadingExchange = false;
        state.error = action.payload;
      })

      // Get Exchange Item Details
      .addCase(getExchangeItemDetailsThunk.pending, (state) => {
        state.loadingExchange = true;
        state.error = null;
      })
      .addCase(getExchangeItemDetailsThunk.fulfilled, (state, action) => {
        state.loadingExchange = false;
        state.exchangeItemDetails = action.payload.data;
      })
      .addCase(getExchangeItemDetailsThunk.rejected, (state, action) => {
        state.loadingExchange = false;
        state.error = action.payload;
      })

      // Verify Exchange Item
      .addCase(verifyExchangeItemThunk.pending, (state) => {
        state.loadingExchange = true;
      })
      .addCase(verifyExchangeItemThunk.fulfilled, (state, action) => {
        state.loadingExchange = false;
        state.successMessage =
          action.payload.message || "Exchange item verified successfully";
        // Update item in pending list
        state.pendingExchangeItems = state.pendingExchangeItems.filter(
          (item) => item._id !== action.meta.arg.exchangeItemId
        );
      })
      .addCase(verifyExchangeItemThunk.rejected, (state, action) => {
        state.loadingExchange = false;
        state.error = action.payload;
      })

      // Reject Exchange Item
      .addCase(rejectExchangeItemThunk.pending, (state) => {
        state.loadingExchange = true;
      })
      .addCase(rejectExchangeItemThunk.fulfilled, (state, action) => {
        state.loadingExchange = false;
        state.successMessage =
          action.payload.message || "Exchange item rejected";
        // Update item in pending list
        state.pendingExchangeItems = state.pendingExchangeItems.filter(
          (item) => item._id !== action.meta.arg.exchangeItemId
        );
      })
      .addCase(rejectExchangeItemThunk.rejected, (state, action) => {
        state.loadingExchange = false;
        state.error = action.payload;
      })

      // Bulk Verify Exchange Items
      .addCase(bulkVerifyExchangeItemsThunk.pending, (state) => {
        state.loadingExchange = true;
      })
      .addCase(bulkVerifyExchangeItemsThunk.fulfilled, (state, action) => {
        state.loadingExchange = false;
        state.successMessage =
          action.payload.message || "Items verified successfully";
        // Refresh pending list would be needed
        const verifiedIds =
          action.payload.data?.success?.map((item) => item.exchangeItemId) ||
          [];
        state.pendingExchangeItems = state.pendingExchangeItems.filter(
          (item) => !verifiedIds.includes(item._id)
        );
      })
      .addCase(bulkVerifyExchangeItemsThunk.rejected, (state, action) => {
        state.loadingExchange = false;
        state.error = action.payload;
      })

      // Get Cart Analytics
      .addCase(getCartAnalyticsThunk.pending, (state) => {
        state.loadingAnalytics = true;
        state.error = null;
      })
      .addCase(getCartAnalyticsThunk.fulfilled, (state, action) => {
        state.loadingAnalytics = false;
        state.cartAnalytics = action.payload.data;
      })
      .addCase(getCartAnalyticsThunk.rejected, (state, action) => {
        state.loadingAnalytics = false;
        state.error = action.payload;
      })

      // Get Abandoned Carts
      .addCase(getAbandonedCartsThunk.pending, (state) => {
        state.loadingAnalytics = true;
        state.error = null;
      })
      .addCase(getAbandonedCartsThunk.fulfilled, (state, action) => {
        state.loadingAnalytics = false;
        state.abandonedCarts = action.payload.data || [];
        state.abandonedPagination =
          action.payload.pagination || initialState.abandonedPagination;
      })
      .addCase(getAbandonedCartsThunk.rejected, (state, action) => {
        state.loadingAnalytics = false;
        state.error = action.payload;
      })

      // Mark Abandoned Carts
      .addCase(markAbandonedCartsThunk.pending, (state) => {
        state.loadingAnalytics = true;
      })
      .addCase(markAbandonedCartsThunk.fulfilled, (state, action) => {
        state.loadingAnalytics = false;
        state.successMessage =
          action.payload.message || "Carts marked as abandoned";
      })
      .addCase(markAbandonedCartsThunk.rejected, (state, action) => {
        state.loadingAnalytics = false;
        state.error = action.payload;
      });
  },
});

export const {
  setCartFilters,
  clearCartFilters,
  setExchangeFilters,
  clearExchangeFilters,
  clearSuccessMessage,
} = adminCartSlice.actions;

export default adminCartSlice.reducer;
